// "Copy from another instrument": pick any published version from the
// catalogue, tick whole sections or individual questions, and bring them into
// the current draft as fresh copies. Copies get brand-new ids (a question's
// analysis history never follows it between instruments); follow-up questions
// ride along with their parent; safeguarding/governance designations carry
// their source's effective meaning.

import { useEffect, useState, type JSX } from 'react';
import {
  newInstrumentId,
  type InstrumentDefinition,
  type InstrumentItem,
  type InstrumentSection,
} from '@cca/parishes-shared';
import { healthApi, type InstrumentVersionsResponse } from '../api';
import { partitionFollowUps, renderInstrumentText } from '../markup';

export interface CopyPayload {
  newSections: InstrumentSection[];
  intoSectionId?: string;
  items?: { scorecard: InstrumentItem[]; reflection: InstrumentItem[] };
  copiedItemCount: number;
}

/** Clone one item with a fresh id, registering the id swap for parent remaps. */
function cloneItem(
  item: InstrumentItem,
  kind: 'c' | 'q',
  idMap: Map<string, string>,
): InstrumentItem {
  const fresh = newInstrumentId(kind);
  idMap.set(item.id, fresh);
  return { ...structuredClone(item), id: fresh };
}

export function CopyFromOverlay({
  versions,
  draftSections,
  onClose,
  onCopy,
}: {
  versions: InstrumentVersionsResponse;
  draftSections: InstrumentSection[];
  onClose: () => void;
  onCopy: (payload: CopyPayload) => void;
}): JSX.Element {
  const [familyId, setFamilyId] = useState(versions.families[0]?.id ?? '');
  const [version, setVersion] = useState('');
  const [source, setSource] = useState<InstrumentDefinition | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkedSections, setCheckedSections] = useState<Set<string>>(new Set());
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [intoSectionId, setIntoSectionId] = useState(draftSections[0]?.id ?? '');

  const family = versions.families.find((f) => f.id === familyId) ?? null;

  // Family choice drives the version default (its default, else latest).
  useEffect(() => {
    if (!family || family.versions.length === 0) {
      setVersion('');
      return;
    }
    setVersion(
      family.versions.find((v) => v.is_default)?.version ??
        family.versions[family.versions.length - 1]?.version ??
        '',
    );
    // The family object identity changes with every versions refresh; keying
    // on the id alone is deliberate.
  }, [familyId]);

  useEffect(() => {
    if (!version) {
      setSource(null);
      return;
    }
    let cancelled = false;
    setSource(null);
    setError(null);
    healthApi
      .instrument(version)
      .then((res) => {
        if (cancelled) return;
        setSource(res.definition);
        setCheckedSections(new Set());
        setCheckedItems(new Set());
      })
      .catch(() => {
        if (!cancelled) setError('That version could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  const toggle = (set: Set<string>, id: string): Set<string> => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const looseCount = checkedItems.size;
  const selectionEmpty = checkedSections.size === 0 && looseCount === 0;
  const needsTarget = looseCount > 0;
  const noTarget = needsTarget && !intoSectionId;

  const build = (): CopyPayload => {
    const src = source as InstrumentDefinition;
    const newSections: InstrumentSection[] = [];
    const loose: { scorecard: InstrumentItem[]; reflection: InstrumentItem[] } = {
      scorecard: [],
      reflection: [],
    };
    let copiedItemCount = 0;

    for (const section of src.sections) {
      if (checkedSections.has(section.id)) {
        const idMap = new Map<string, string>();
        const scorecard = section.scorecard.map((it) => cloneItem(it, 'c', idMap));
        const reflection = section.reflection.map((it) => cloneItem(it, 'q', idMap));
        for (const q of reflection) {
          if (!q.parentId) continue;
          const mapped = idMap.get(q.parentId);
          if (mapped) q.parentId = mapped;
          else delete q.parentId;
        }
        newSections.push({
          ...structuredClone(section),
          id: newInstrumentId('sec'),
          scorecard,
          reflection,
        });
        continue;
      }
      const { followUps } = partitionFollowUps(section);
      const takeFollowUps = (parent: InstrumentItem, freshParentId: string): void => {
        for (const fu of followUps.get(parent.id) ?? []) {
          const idMap = new Map<string, string>();
          const copy = cloneItem(fu, 'q', idMap);
          copy.parentId = freshParentId;
          loose.reflection.push(copy);
        }
      };
      for (const it of section.scorecard) {
        if (!checkedItems.has(it.id)) continue;
        const idMap = new Map<string, string>();
        const copy = cloneItem(it, 'c', idMap);
        // Carry the source's effective designation explicitly, so the copy
        // means the same thing wherever it lands.
        const effective = it.designation ?? section.designation;
        if (effective) copy.designation = effective;
        else delete copy.designation;
        loose.scorecard.push(copy);
        takeFollowUps(it, copy.id);
        copiedItemCount++;
      }
      const { topReflection } = partitionFollowUps(section);
      for (const q of topReflection) {
        if (!checkedItems.has(q.id)) continue;
        const idMap = new Map<string, string>();
        const copy = cloneItem(q, 'q', idMap);
        delete copy.parentId;
        loose.reflection.push(copy);
        takeFollowUps(q, copy.id);
        copiedItemCount++;
      }
    }

    return {
      newSections,
      copiedItemCount,
      ...(looseCount > 0 ? { intoSectionId, items: loose } : {}),
    };
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop click duplicates the explicit Close button inside the dialog
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(38, 34, 27, 0.45)',
        zIndex: 50,
        overflowY: 'auto',
        padding: '40px 16px',
      }}
      onClick={onClose}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click handler only stops the backdrop-dismiss propagating; not an interaction */}
      <div
        className="card"
        style={{ maxWidth: 760, margin: '0 auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="health-section-heading">
          <span>Copy from another instrument</span>
          <button type="button" className="instr-ctl" onClick={onClose}>
            Close
          </button>
        </h2>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            className="instr-field instr-field-wide"
            value={familyId}
            aria-label="Instrument to copy from"
            onChange={(e) => setFamilyId(e.target.value)}
          >
            {versions.families.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
          <select
            className="instr-field"
            value={version}
            aria-label="Version to copy from"
            onChange={(e) => setVersion(e.target.value)}
          >
            {(family?.versions ?? []).map((v) => (
              <option key={v.version} value={v.version}>
                {v.version}
                {v.is_default ? ' (default)' : ''}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="health-banner health-banner-red" role="alert" style={{ marginTop: 12 }}>
            {error}
          </div>
        )}
        {!source && !error && (
          <p style={{ fontSize: 13, color: 'var(--health-grey)', marginTop: 12 }}>Loading...</p>
        )}

        {source && (
          <>
            <p style={{ fontSize: 12.5, color: 'var(--health-grey)', margin: '10px 0 4px' }}>
              Tick whole sections (added to the end of the draft) or individual questions. Follow-up
              questions come along with their parent. Copies are independent: editing one never
              changes the other.
            </p>
            {source.sections.map((section) => {
              const whole = checkedSections.has(section.id);
              const { topReflection } = partitionFollowUps(section);
              return (
                <div
                  key={section.id}
                  style={{ borderTop: '1px solid var(--health-border-soft)', padding: '8px 0' }}
                >
                  <label
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'baseline',
                      fontWeight: 600,
                      fontSize: 13.5,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={whole}
                      onChange={() => setCheckedSections((s) => toggle(s, section.id))}
                    />
                    <span>
                      {section.no}. {renderInstrumentText(section.title || 'Untitled')}
                    </span>
                    {section.designation && (
                      <span className="pill pill-neutral">{section.designation}</span>
                    )}
                  </label>
                  {!whole && (
                    <div style={{ paddingLeft: 24, marginTop: 4, display: 'grid', gap: 3 }}>
                      {[...section.scorecard, ...topReflection].map((it) => (
                        <label
                          key={it.id}
                          style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'baseline',
                            fontSize: 13,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checkedItems.has(it.id)}
                            onChange={() => setCheckedItems((s) => toggle(s, it.id))}
                          />
                          <span>{renderInstrumentText(it.text || '(no text)')}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                flexWrap: 'wrap',
                marginTop: 14,
              }}
            >
              {needsTarget && (
                <label
                  style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 13 }}
                >
                  Add ticked questions to
                  <select
                    className="instr-field"
                    value={intoSectionId}
                    aria-label="Section to copy the questions into"
                    onChange={(e) => setIntoSectionId(e.target.value)}
                  >
                    {draftSections.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.no}. {s.title || 'Untitled'}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <button
                type="button"
                className="btn btn-primary"
                disabled={selectionEmpty || noTarget}
                onClick={() => onCopy(build())}
              >
                Copy into the draft
              </button>
              {noTarget && (
                <span style={{ fontSize: 12.5, color: 'var(--health-grey)' }}>
                  Add a section to the draft first, so the questions have somewhere to go.
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
